import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ZnSingleSelect } from '../../../../../shared/src/lib/zn-select/zn-single-select';
import { ZnSimpleSelect } from '../../../../../shared/src/lib/zn-select/zn-simple-select';
import {
  maxDecimalsValidator,
  localizeWithFallback,
  noFutureDateValidator,
  noWhitespaceValidator,
  futureDateDisabled,
  handleDateBlur,
  REASON_GROUP_NAME_AREA_ADJUSTMENT,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  DEFAULT_TOASTER_LIFE,
} from '@zendra/shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { DateMaskDirective } from '../../../../../shared/src/lib/directives/date-mask.directive';
import { catchError, EMPTY, finalize, startWith } from 'rxjs';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { GeneticMaterialSelectOption, StandCatalogs } from './services/stand-catalogs';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { SelectOption } from './types/stand-form.types';
import {
  syncSpeciesWithGeneticMaterialFromForm,
} from '../helpers/genetic-material-species.helper';
import { buildStandRequest } from '../helpers/stand.builder';
import { NgbActiveModal, NgbDateParserFormatter, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { StandPlanningOrchestratorService } from 'orchestrator';
import { StandGis } from '../stand-gis/stand-gis';
import { DEFAULT_STAND_TYPE, STAND_CREATION_FORM_MAX_LENGTHS } from './stand-creation-form.constants';
import type { SpeciesDto } from 'projects/cbd-proxies/src/lib/general/dtos/species/models';
import { CustomDateParserFormatter } from '../../../../../shared/src/lib/utils/custom-date-parser-formatter';

@Component({
  selector: 'stand-creation-form',
  standalone: true,
  imports: [
    CommonModule,
    StandGis,
    ZnSingleSelect,
    ZnSimpleSelect,
    NgbDatepickerModule,
    LocalizationPipe,
    ReactiveFormsModule,
    DateMaskDirective,
  ],
  templateUrl: './stand-creation-form.html',
  styleUrl: './stand-creation-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class StandCreationForm implements IModalContent {
  readonly standNameMaxLength = STAND_CREATION_FORM_MAX_LENGTHS.standName;
  readonly standAreaMaintenanceNotesMaxLength = STAND_CREATION_FORM_MAX_LENGTHS.standAreaMaintenanceNotes;
  readonly today = (() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
  })();

  private readonly fb = inject(FormBuilder);
  private readonly standCatalogService = inject(StandCatalogs);

  private readonly toaster = inject(ToasterService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly standService = inject(StandPlanningOrchestratorService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly localizationService = inject(LocalizationService);

  hasNearbyPopulationOptions: SelectOption<string>[] = [
    { key: 'true', value: 'Option:Yes' },
    { key: 'false', value: 'Option:No' },
  ];

  localizationResource = ZendraLocalizationServices.LanguageService;

  loading = signal(true);
  saving = signal(false);
  isPlanted = signal(false);
  propertyOptions = signal<SelectOption<string>[]>([]);
  standTypeOptions = signal<SelectOption<string>[]>([]);
  periodOptions = signal<SelectOption<string>[]>([]);
  reasonOptions = signal<SelectOption<string>[]>([]);
  adjustmentTypeOptions = signal<SelectOption<string>[]>([]);
  plantingTypeOptions = signal<SelectOption<string>[]>([]);
  spacingOptions = signal<SelectOption<string>[]>([]);
  geneticMaterialOptions = signal<GeneticMaterialSelectOption[]>([]);
  species = signal<SelectOption<string>[]>([]);
  selectedPlantationSpecies = signal<SpeciesDto | null>(null);
  disabledDates = futureDateDisabled;
  private readonly pendingGeneticMaterialRequests = new Set<string>();

  cycleRotationRequired = signal(false);

  form: FormGroup = this.fb.group({
    StandName: [
      '',
      [Validators.required, Validators.maxLength(this.standNameMaxLength), noWhitespaceValidator],
    ],
    StandArea: ['', [Validators.required, Validators.min(0.01), maxDecimalsValidator(2)]],
    GisArea: [0],
    PropertyId: [null, [Validators.required]],
    StandTypeId: [null, [Validators.required]],
    HasNearbyPopulation: [null, [Validators.required]],
    CurrentCycle: [null, [Validators.min(1), Validators.pattern(/^-?\d+$/)]],
    CurrentRotation: [null, [Validators.min(1), Validators.pattern(/^-?\d+$/)]],
    Period: [null],
    Reason: [null],
    AdjustmentType: [null],
    Notes: ['', [Validators.maxLength(this.standAreaMaintenanceNotesMaxLength)]],
    IsAreaUpdated: [false],
    IsPlanted: [false],
    PlantingDate: [null],
    PlantingType: [null],
    ownPlantation: [false],
    Spacing: [null],
    GeneticMaterial: [null],
    Species: [{ value: '', disabled: true }],
  });
  private readonly formValue = toSignal(this.formValueChanges(), { initialValue: null });
  private readonly formStatus = toSignal(this.formStatusChanges(), {
    initialValue: this.form.status,
  });

  readonly isMaintenanceTouched = computed(() => {
    this.formValue();
    return ['Period', 'Reason', 'AdjustmentType', 'Notes', 'IsAreaUpdated'].some(key => {
      const ctrl = this.form.get(key);
      return !!ctrl?.value && (ctrl.dirty || ctrl.touched);
    });
  });

  // Returns id if backends need it
  readonly speciesValue = computed(() => {
    return this.selectedPlantationSpecies()?.id ?? '';
  });

  // Name to display in form
  readonly speciesDisplayValue = computed(() => {
    return this.selectedPlantationSpecies()?.name ?? '';
  });

  canFetchGisArea = computed(() => {
    this.formValue();
    return !!this.form.get('StandName')?.value;
  });

  canEnablePlantation = computed(() => {
    this.formValue();
    return (
      !!this.form.get('CurrentCycle')?.value && !!this.form.get('CurrentRotation')?.value
    );
  });

  plantationSectionRequired = computed(() => {
    return this.isPlanted();
  });

  isMaintenanceSectionRequired = computed(() => {
    this.formValue();
    return this.isMaintenanceTouched();
  });

  canSave = computed(() => {
    this.formStatus();
    return this.form.valid;
  });

  readonly loadProperty = (params?: ZnSelectQueryParams) => this.standCatalogService.loadProperty(params);
  readonly loadsSandType = (params?: ZnSelectQueryParams) => this.standCatalogService.loadsSandType(params);
  readonly loadPeriod = (params?: ZnSelectQueryParams) => this.standCatalogService.loadPeriod(params);
  readonly loadReason = (params?: ZnSelectQueryParams) =>
    this.standCatalogService.loadReason(params, REASON_GROUP_NAME_AREA_ADJUSTMENT);
  readonly loadAdjustmentTypes = (params?: ZnSelectQueryParams) => this.standCatalogService.loadAdjustmentTypes(params);
  readonly plantingTypesLoader = (params?: ZnSelectQueryParams) => this.standCatalogService.loadPlantingTypes(params);
  readonly spacingsLoader = (params?: ZnSelectQueryParams) =>this.standCatalogService.loadSpacings(params);
  readonly geneticMaterialsLoader = (params?: ZnSelectQueryParams) =>this.standCatalogService.loadGeneticMaterials(params);

  constructor() {
    this.loadDefaultsValues();
    this.setupDynamicValidators();

    effect(() => {
      const planted = this.isPlanted();
      untracked(() => this.updatePlantationValidators(planted));
    });

    effect(() => {
      this.formValue();
      untracked(() => this.syncSelectedGeneticMaterialSpecies());
    });
  }
  private formValueChanges() {
    return this.form.valueChanges.pipe(startWith(this.form.getRawValue()));
  }

  private formStatusChanges() {
    return this.form.statusChanges.pipe(startWith(this.form.status));
  }

  private loadDefaultsValues() {
    this.loadsSandType()
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => { this.loading.set(false) })
    )
    .subscribe({
      next: result => {
        const defaultStandType = result.items.find(
            item => String(item.value).toLocaleUpperCase() === DEFAULT_STAND_TYPE,
          );
          if (defaultStandType) {
            this.form.get('StandTypeId')?.setValue(defaultStandType.key, { emitEvent: false });
          }
      },
      error: error => {
        console.log(error);
      }
    });
  }

  fieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  private applySpeciesSelection(species?: SpeciesDto | null): void {
    this.selectedPlantationSpecies.set(species ?? null);
    this.form.get('Species')?.patchValue(species?.id ?? null, { emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private syncSelectedGeneticMaterialSpecies(): void {
    syncSpeciesWithGeneticMaterialFromForm({
      destroyRef: this.destroyRef,
      form: this.form,
      geneticMaterialOptions: this.geneticMaterialOptions,
      geneticMaterialsLoader: this.geneticMaterialsLoader,
      applySpeciesSelection: species => this.applySpeciesSelection(species),
      loading: this.loading,
      pendingRequestIds: this.pendingGeneticMaterialRequests,
    });
  }

  private setupDynamicValidators() {
    this.form.valueChanges
      .pipe(startWith(this.form.getRawValue()), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const cycle = this.form.get('CurrentCycle')?.value;
        const rotation = this.form.get('CurrentRotation')?.value;
        const maintenanceTouched = this.isMaintenanceTouched();
        const planted = this.isPlanted();

        this.updateCycleRotationValidators(cycle, rotation, maintenanceTouched);
        this.updateMaintenanceValidators(maintenanceTouched);
        this.updatePlantationValidators(planted);
      });
  }

  private updateCycleRotationValidators(
    cycle: unknown,
    rotation: unknown,
    maintenanceTouched: boolean,
  ) {
    const required = !!cycle || !!rotation || maintenanceTouched;
    this.cycleRotationRequired.set(required);

    const validators = required
      ? [Validators.required, Validators.min(1), Validators.pattern(/^-?\d+$/)]
      : [Validators.min(1), Validators.pattern(/^-?\d+$/)];

    this.form.get('CurrentCycle')?.setValidators(validators);
    this.form.get('CurrentRotation')?.setValidators(validators);
    this.form.get('CurrentCycle')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('CurrentRotation')?.updateValueAndValidity({ emitEvent: false });

    const vaildCycleRotation =
      this.form.get('CurrentCycle')?.valid && this.form.get('CurrentRotation')?.valid;
    const canPlant = !!cycle && !!rotation && vaildCycleRotation;
    const isPlantedCtrl = this.form.get('IsPlanted');

    if (canPlant) {
      isPlantedCtrl?.enable({ emitEvent: false });
    } else {
      isPlantedCtrl?.setValue(false, { emitEvent: false });
      isPlantedCtrl?.disable({ emitEvent: false });
      this.isPlanted.set(false);
    }
  }

  private updateMaintenanceValidators(maintenanceTouched: boolean) {
    const fields = ['Period', 'Reason', 'AdjustmentType'];
    fields.forEach(key => {
      const ctrl = this.form.get(key);
      if (maintenanceTouched) {
        ctrl?.setValidators([Validators.required]);
      } else {
        ctrl?.clearValidators();
      }
      ctrl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private updatePlantationValidators(planted: boolean) {
    const plantingFields = ['PlantingType', 'Spacing', 'GeneticMaterial'];

    if (planted) {
      this.form
        .get('PlantingDate')
        ?.setValidators([Validators.required, noFutureDateValidator()]);
      plantingFields.forEach(key => {
        this.form.get(key)?.setValidators([Validators.required]);
      });
    } else {
      this.form.get('PlantingDate')?.clearValidators();
      plantingFields.forEach(key => this.form.get(key)?.clearValidators());
    }

    this.form.get('PlantingDate')?.updateValueAndValidity({ emitEvent: false });
    plantingFields.forEach(key => {
      this.form.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  togglePlanted(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.isPlanted.set(checked);
    this.form.get('IsPlanted')?.setValue(checked);
  }

  onPlantingDateBlur(inputElement: HTMLInputElement): void {
    handleDateBlur(inputElement, this.form.get('PlantingDate'));
  }

  onUpdateGisArea(gisArea: number) {
    const roundedGisArea = Math.round((gisArea + Number.EPSILON) * 100) / 100;
    this.form.patchValue({
      StandArea: roundedGisArea,
      GisArea: roundedGisArea,
    });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.saving()) {
      return;
    }

    this.form.get('Species')?.setValue(this.speciesValue(), { emitEvent: false });
    const form = this.form.getRawValue();
    const stand = buildStandRequest(form, this.isPlanted(), this.isMaintenanceTouched());
    this.saving.set(true);

    this.standService
      .createStandWithDetails(stand)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: response => {
          console.log(response);
          this.toaster.success(`${this.localizationResource}::MessageSaveSuccess`, '', {
            life: DEFAULT_TOASTER_LIFE,
          });
          this.form.markAsPristine();
          this.activeModal.close(response);
        },
      });
  }

  confirm(): void {
    this.save();
  }
  dismiss(): void {
    if (this.saving()) {
      return;
    }

    if(this.form.dirty) {
      this.showCancelConfirmation().subscribe(status => {
      if (status === Confirmation.Status.confirm) {
        this.form.reset();
        this.activeModal.dismiss();
      }
    });
    } else {
      this.activeModal.dismiss();
    }
  }

  private showCancelConfirmation() {
    return this.confirmationService.show(
      localizeWithFallback(
        this.localizationService,
        'LanguageService::ConfirmCancelChanges',
      ),
      localizeWithFallback(
        this.localizationService,
        'LanguageService::ConfirmCancelChangesTitle',
      ),
      'warning',
      {
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
        yesText: localizeWithFallback(
          this.localizationService,
          'LanguageService::Option:Yes',
        ),
        cancelText: localizeWithFallback(
          this.localizationService,
          'LanguageService::Option:No',
        ),
      },
    );
  }
}
